import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Divider,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Groups as TeamIcon,
    Person as PersonIcon,
    SupervisorAccount as AdminIcon,
} from '@mui/icons-material';
import TrendChart from '../components/TrendChart';
import TeamTrendChart from '../components/TeamTrendChart';
import OsobniStatistikaCard, { StatistikaCardContent } from '../components/OsobniStatistikaCard';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/cs';
import { useQuery } from '@tanstack/react-query';
import JsdhNotifikaceService from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import type { UdalostStatistiky, StatistikaPoplach, DashboardStats } from '../api/JsdhNotifikaceService/generated';
import { useAuthStore } from '../store/auth-store';
import { formatujDobuReakce } from '../utils/statistikyUtils';
import { useAllUsersQuery } from '../api/JsdhNotifikaceService/service/AdminMutations';
import { useStatistikaUzivateleQuery } from '../api/JsdhNotifikaceService/service/StatistikaMutations';
import type { TymoveGrafyResponse } from '../api/JsdhNotifikaceService/generated/models/TymoveGrafy';
import { formatAdresa } from '../utils/adresaUdalosti';

const StatCard: React.FC<{
    title: string;
    value: string | number;
    subtitle?: string;
    color?: string;
}> = ({ title, value, subtitle, color }) => (
    <Card>
        <CardContent>
            <Typography color="text.secondary" variant="body2" gutterBottom>
                {title}
            </Typography>
            <Typography variant="h3" component="div" sx={{ color }}>
                {value}
            </Typography>
            {subtitle && (
                <Typography variant="body2" color="text.secondary">
                    {subtitle}
                </Typography>
            )}
        </CardContent>
    </Card>
);

// Tab types
type StatistikyTab = 'team' | 'personal' | 'user';
type DatePreset = '7d' | '30d' | 'year' | null;

const StatistikyPage: React.FC = () => {
    const { isAdmin } = useAuthStore();
    const [activeTab, setActiveTab] = useState<StatistikyTab>('team');
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs().subtract(30, 'day'));
    const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs());
    const [selectedPreset, setSelectedPreset] = useState<DatePreset>('30d');

    // Load users for admin dropdown
    const { data: users } = useAllUsersQuery();
    const activeUsers = users?.filter(u => u.aktivni) || [];

    // Load selected user stats (for admin view)
    const { data: selectedUserStats, isLoading: userStatsLoading } = useStatistikaUzivateleQuery(
        activeTab === 'user' ? selectedUserId : null
    );

    const params = {
        dateFrom: dateFrom?.format('YYYY-MM-DD'),
        dateTo: dateTo?.format('YYYY-MM-DD'),
    };

    const { data: statistiky, isLoading: statistikyLoading } = useQuery<UdalostStatistiky>({
        queryKey: ['udalostStatistiky', params],
        queryFn: () => JsdhNotifikaceService.getUdalostStatistiky(params),
        enabled: activeTab === 'team',
    });

    const { data: poplachy, isLoading: poplachyLoading } = useQuery<StatistikaPoplach[]>({
        queryKey: ['statistikaPoplachy'],
        queryFn: () => JsdhNotifikaceService.getStatistikaPoplachy(10),
        enabled: activeTab === 'team',
    });

    // Dashboard stats s filtrováním podle vybraného období
    const dashboardParams = {
        od: dateFrom?.format('YYYY-MM-DD'),
        doCasu: dateTo?.format('YYYY-MM-DD'),
    };

    const { data: dashboardStats, isLoading: dashboardLoading } = useQuery<DashboardStats>({
        queryKey: ['dashboardStats', dashboardParams],
        queryFn: () => JsdhNotifikaceService.getDashboardStats(dashboardParams),
        enabled: activeTab === 'team',
    });

    // Team charts data
    const { data: tymoveGrafy, isLoading: tymoveGrafyLoading, error: tymoveGrafyError } = useQuery<TymoveGrafyResponse>({
        queryKey: ['tymoveGrafy'],
        queryFn: () => JsdhNotifikaceService.getTymoveGrafy(),
        enabled: activeTab === 'team',
    });

    const isLoading = activeTab === 'team'
        ? (statistikyLoading || poplachyLoading || dashboardLoading || tymoveGrafyLoading)
        : activeTab === 'user'
            ? userStatsLoading
            : false;

    const handleTabChange = (_: React.SyntheticEvent, newValue: StatistikyTab) => {
        setActiveTab(newValue);
    };

    const handleDateFromChange = (value: Dayjs | null) => {
        setDateFrom(value);
        setSelectedPreset(null);
    };

    const handleDateToChange = (value: Dayjs | null) => {
        setDateTo(value);
        setSelectedPreset(null);
    };

    const handlePresetChange = (_: React.MouseEvent<HTMLElement>, newPreset: DatePreset | null) => {
        if (!newPreset) return;
        setSelectedPreset(newPreset);
        switch (newPreset) {
            case '7d':
                setDateFrom(dayjs().subtract(7, 'day'));
                setDateTo(dayjs());
                break;
            case '30d':
                setDateFrom(dayjs().subtract(30, 'day'));
                setDateTo(dayjs());
                break;
            case 'year':
                setDateFrom(dayjs().startOf('year'));
                setDateTo(dayjs());
                break;
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Statistiky
                </Typography>

                {/* Tabs for switching views */}
                <Paper sx={{ mb: 3 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab
                            value="team"
                            label="Tým"
                            icon={<TeamIcon />}
                            iconPosition="start"
                        />
                        <Tab
                            value="personal"
                            label="Moje statistiky"
                            icon={<PersonIcon />}
                            iconPosition="start"
                        />
                        {isAdmin && (
                            <Tab
                                value="user"
                                label="Statistiky uživatele"
                                icon={<AdminIcon />}
                                iconPosition="start"
                            />
                        )}
                    </Tabs>
                </Paper>

                {/* Admin user selector */}
                {activeTab === 'user' && isAdmin && (
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Vyberte uživatele</InputLabel>
                            <Select
                                value={selectedUserId || ''}
                                onChange={(e) => setSelectedUserId(e.target.value as number)}
                                label="Vyberte uživatele"
                            >
                                {activeUsers.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {u.jmeno} {u.funkce ? `(${u.funkce})` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Paper>
                )}

                {/* Personal Statistics Tab */}
                {activeTab === 'personal' && (
                    <Box sx={{ mb: 3 }}>
                        <OsobniStatistikaCard />
                    </Box>
                )}

                {/* User Statistics Tab (Admin only) */}
                {activeTab === 'user' && isAdmin && (
                    <Box sx={{ mb: 3 }}>
                        {!selectedUserId ? (
                            <Alert severity="info">
                                Vyberte uživatele pro zobrazení jeho statistik
                            </Alert>
                        ) : userStatsLoading ? (
                            <Box display="flex" justifyContent="center" p={4}>
                                <CircularProgress />
                            </Box>
                        ) : selectedUserStats ? (
                            <StatistikaCardContent
                                stats={selectedUserStats}
                                title={`Statistiky uživatele: ${selectedUserStats.jmeno}`}
                            />
                        ) : (
                            <Alert severity="warning">
                                Statistiky pro vybraného uživatele nebyly nalezeny
                            </Alert>
                        )}
                    </Box>
                )}

                {/* Team Statistics Tab */}
                {activeTab === 'team' && (
                    <>
                        {/* Date Range Filter */}
                        <Paper sx={{ p: 2, mb: 3 }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <DatePicker
                                        label="Od data"
                                        value={dateFrom}
                                        onChange={handleDateFromChange}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <DatePicker
                                        label="Do data"
                                        value={dateTo}
                                        onChange={handleDateToChange}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <ToggleButtonGroup
                                        value={selectedPreset}
                                        exclusive
                                        onChange={handlePresetChange}
                                        size="small"
                                    >
                                        <ToggleButton value="7d">7 dní</ToggleButton>
                                        <ToggleButton value="30d">30 dní</ToggleButton>
                                        <ToggleButton value="year">Rok</ToggleButton>
                                    </ToggleButtonGroup>
                                </Grid>
                            </Grid>
                        </Paper>

                {isLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Summary Stats — merged into single row of 6 */}
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                <StatCard
                                    title="Poplachů celkem"
                                    value={dashboardStats?.celkemPoplachu ?? 0}
                                    subtitle="historicky"
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                <StatCard
                                    title="Poplachů (období)"
                                    value={dashboardStats?.vybraneObdobi?.pocetPoplachu ?? statistiky?.pocetPoplachu ?? 0}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                <StatCard
                                    title="Odpovědí"
                                    value={dashboardStats?.vybraneObdobi?.pocetOdpovedi ?? statistiky?.pocetOdpovedi ?? 0}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                <StatCard
                                    title="JEDU"
                                    value={dashboardStats?.vybraneObdobi?.pocetJedu ?? statistiky?.pocetJedu ?? 0}
                                    color="success.main"
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                <StatCard
                                    title="NEJEDU"
                                    value={dashboardStats?.vybraneObdobi?.pocetNejedu ?? statistiky?.pocetNejedu ?? 0}
                                    color="error.main"
                                />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                                <StatCard
                                    title="Prům. reakce"
                                    value={formatujDobuReakce(
                                        dashboardStats?.vybraneObdobi?.prumernaDobaReakce
                                            ?? dashboardStats?.prumernaDobaReakce
                                            ?? null
                                    )}
                                />
                            </Grid>
                        </Grid>

                        {/* Trend Chart */}
                        <Box sx={{ mb: 3 }}>
                            <TrendChart
                                poplachy={poplachy || []}
                                podleTypu={statistiky?.podleTypu}
                            />
                        </Box>

                        {/* Team Charts */}
                        {tymoveGrafyLoading ? (
                            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress />
                            </Paper>
                        ) : tymoveGrafyError ? (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                Chyba při načítání týmových grafů: {(tymoveGrafyError as Error).message}
                            </Alert>
                        ) : tymoveGrafy ? (
                            <Box sx={{ mb: 3 }}>
                                <TeamTrendChart data={tymoveGrafy} />
                            </Box>
                        ) : (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                Data pro týmové grafy nejsou k dispozici
                            </Alert>
                        )}

                        <Grid container spacing={3}>
                            {/* Recent Alarms */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Poslední poplachy
                                    </Typography>
                                    <List dense>
                                        {poplachy?.map((poplach, index) => (
                                            <React.Fragment key={poplach.poplachId}>
                                                <ListItem>
                                                    <ListItemText
                                                        primary={poplach.udalost || 'Neznámý typ'}
                                                        secondary={
                                                            <>
                                                                {poplach.casVyhlaseni &&
                                                                    new Date(poplach.casVyhlaseni).toLocaleString('cs-CZ')}
                                                                {' - '}
                                                                {formatAdresa(poplach) || 'Neznámé místo'}
                                                            </>
                                                        }
                                                    />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {poplach.pocetOdpovedi || 0} odpovědí
                                                    </Typography>
                                                </ListItem>
                                                {index < (poplachy?.length || 0) - 1 && <Divider />}
                                            </React.Fragment>
                                        ))}
                                        {(!poplachy || poplachy.length === 0) && (
                                            <ListItem>
                                                <ListItemText secondary="Žádné poplachy" />
                                            </ListItem>
                                        )}
                                    </List>
                                </Paper>
                            </Grid>

                            {/* Top Members */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Nejaktivnější členové
                                    </Typography>
                                    <List dense>
                                        {dashboardStats?.nejaktivnejsiClenove?.map((member, index) => (
                                            <React.Fragment key={index}>
                                                <ListItem>
                                                    <ListItemText
                                                        primary={
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        width: 24,
                                                                        height: 24,
                                                                        borderRadius: '50%',
                                                                        bgcolor: 'primary.main',
                                                                        color: 'white',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: '0.75rem',
                                                                    }}
                                                                >
                                                                    {index + 1}
                                                                </Typography>
                                                                {member.jmeno}
                                                            </Box>
                                                        }
                                                    />
                                                    <Typography variant="body2" color="success.main">
                                                        {member.pocetOdpovedi} odpovědí
                                                    </Typography>
                                                </ListItem>
                                                {index < (dashboardStats?.nejaktivnejsiClenove?.length || 0) - 1 && (
                                                    <Divider />
                                                )}
                                            </React.Fragment>
                                        ))}
                                        {(!dashboardStats?.nejaktivnejsiClenove || dashboardStats.nejaktivnejsiClenove.length === 0) && (
                                            <ListItem>
                                                <ListItemText secondary="Žádná data" />
                                            </ListItem>
                                        )}
                                    </List>
                                </Paper>
                            </Grid>
                        </Grid>
                    </>
                )}
                    </>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default StatistikyPage;
